import { useEffect, useState } from 'react';
import { cn } from '@/lib/utils';

/**
 * CurrentTimeLine (PROD-981) — the live "now" indicator drawn across a
 * calendar time-grid column. Flutter parity: the calendar grids highlight
 * the current time with a blue marker that ticks live (calendar_by_week /
 * _room / _doctor widgets). Here it's a thin brand-blue rule with a dot at
 * the left edge, positioned by minute-of-day against the same `startHour` /
 * `hourHeight` the tiles use, so it lines up exactly.
 *
 * Renders nothing when "now" falls outside the visible hour window. The
 * caller is responsible for mounting it ONLY when the displayed day is today
 * (so week/by-doctor/by-room don't draw it on other days). Self-updates once
 * a minute while mounted.
 */

function nowMinutes(): number {
  const d = new Date();
  return d.getHours() * 60 + d.getMinutes();
}

interface CurrentTimeLineProps {
  /** First hour in the gutter (anchors top=0). */
  startHour: number;
  /** Exclusive bottom hour of the grid (e.g. last row hour + 1). */
  endHour: number;
  /** Pixels per hour — must match the grid's row height. */
  hourHeight: number;
  /** Extra positioning classes (e.g. `left-16 right-0` to clear the gutter
   *  in the multi-column by-doctor/by-room grids). Defaults span the column. */
  className?: string;
}

export function CurrentTimeLine({
  startHour,
  endHour,
  hourHeight,
  className,
}: CurrentTimeLineProps) {
  const [minute, setMinute] = useState(nowMinutes);
  useEffect(() => {
    const id = setInterval(() => setMinute(nowMinutes()), 60_000);
    return () => clearInterval(id);
  }, []);

  const startMin = startHour * 60;
  const endMin = endHour * 60;
  if (minute < startMin || minute > endMin) return null;

  const top = ((minute - startMin) / 60) * hourHeight;
  return (
    <div
      className={cn('absolute left-0 right-0 z-20 pointer-events-none', className)}
      style={{ top }}
      aria-hidden
    >
      <div className="border-t-2 border-brand-primary">
        <span className="absolute -left-1 -top-[4px] h-2 w-2 rounded-full bg-brand-primary" />
      </div>
    </div>
  );
}
